import { previewImage } from 'antd/lib/upload/utils';
import { heicWorkerManager } from '../../utils/heicWorkerManager';
import { imageMimeTypes, isHeicFile } from '../../utils/ImageUtils';

/**
 * Build the thumbnail antd shows for one entry in a `picture` / `picture-card` upload list.
 *
 * antd's own `previewImage` draws the file's bytes into a canvas, which no browser can do for an
 * iPhone photo — and it fails in two different ways. Chrome and Firefox report an empty MIME type
 * for the HEIC container, so it resolves `''` straight away; Safari reports `image/heic`, so it
 * builds an `<img>` and waits on `onload`, which never fires and which it has no `onerror` for, so
 * the promise never settles. Either way the entry ends up with no `thumbUrl`, antd's list item
 * falls through to `iconRender`, and the tile shows a generic file icon where the photo should be.
 *
 * Decoding to JPEG first and handing antd that keeps the entry on the ordinary thumbnail path, so
 * the tile's click-to-preview and its label behave exactly as they do for an uploaded JPEG. The
 * local decode is worth paying for at this one spot because the bytes are already in the tab and
 * the tile can show immediately; every surface that renders a *stored* image still prefers the
 * backend's derivative through `ResolvedImage`.
 *
 * A failed decode resolves `''`, which puts the tile back on antd's file icon rather than leaving
 * the promise hanging forever.
 */
export const previewUploadFile = async (file: File | Blob): Promise<string> => {
	// A MIME type the browser can render decides this on its own, ahead of the file name.
	// `isHeicFile` falls back to the extension because Chrome and Firefox report no type at all for
	// the HEIC container, but that fallback must not outvote a type that is already renderable: a
	// JPEG named `.heic` would otherwise be sent into a decode that fails on non-HEIC bytes, and
	// the tile would lose the thumbnail antd could have produced from the file directly.
	const isBrowserRenderableType = imageMimeTypes.indexOf(file.type) !== -1;
	if (isBrowserRenderableType || !isHeicFile(file)) {
		return previewImage(file);
	}
	try {
		const arrayBuffer = await file.arrayBuffer();
		const jpegBlob = await heicWorkerManager.convert(arrayBuffer);
		return previewImage(jpegBlob);
	} catch (error) {
		return '';
	}
};

export default previewUploadFile;
